import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','Free Size','32','34','36','38','40','42'];

/* ─── Stock Aging Table Component ─── */
const StockAgingTable = ({ vendorId, designImages }) => {
  const [agingData, setAgingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/vendor-analytics/${vendorId}/stock-aging-data`);
        setAgingData(res.data || []);
      } catch { toast.error('Failed to load aging data'); }
      setLoading(false);
    };
    fetch();
  }, [vendorId]);

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading aging data...</div>;
  if (!agingData.length) return <div className="text-center py-8 text-gray-400 text-sm">No aging data available</div>;

  const totals = agingData.reduce((acc, r) => {
    Object.keys(r).forEach(k => { if (k !== 'design_code') acc[k] = (acc[k] || 0) + (r[k] || 0); });
    return acc;
  }, {});

  const getBucketColor = (val, type) => {
    if (!val || val <= 0) return '';
    if (type === 'lt30') return 'bg-green-100 text-green-800';
    if (type === 'd30_60') return 'bg-yellow-100 text-yellow-800';
    if (type === 'd60_90') return 'bg-orange-100 text-orange-800';
    if (type === 'gt90') return 'bg-red-100 text-red-800';
    return '';
  };

  return (
    <>
    <div className="overflow-x-auto rounded-lg border border-gray-300" data-testid="stock-aging-table">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[#1B4F72]">
            <th colSpan={10} className="border border-[#1B4F72]"></th>
            <th colSpan={4} className="px-2 py-2 text-center text-white text-xs font-bold uppercase tracking-wider border border-[#1A5276] bg-[#1A5276]">Sale Ageing</th>
            <th colSpan={5} className="px-2 py-2 text-center text-white text-xs font-bold uppercase tracking-wider border border-[#7D3C98] bg-[#7D3C98]">Stock Ageing</th>
          </tr>
          <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
            {['SR','Image','Design','Opening','Purchase','Pur Ret','Sale','Sale Ret','Reserved','Stock','<30','30-60','60-90','>90','<30','30-60','60-90','>90','Stock'].map((h, i) => (
              <th key={i} className="px-2 py-2.5 text-center font-medium border border-gray-700">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {agingData.map((r, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 py-1.5 text-center text-gray-500 border border-gray-400 text-xs">{idx + 1}</td>
              <td className="px-1 py-1 text-center border border-gray-400">
                {designImages?.[r.design_code] ? (
                  <img src={`${API_BASE}${designImages[r.design_code]}`} alt={r.design_code}
                    className="w-10 h-10 rounded object-cover cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all mx-auto"
                    onClick={() => setLightbox(`${API_BASE}${designImages[r.design_code]}`)}
                    data-testid={`aging-img-${r.design_code}`} />
                ) : (
                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-300 mx-auto">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                )}
              </td>
              <td className="px-3 py-1.5 font-bold text-gray-900 border border-gray-400">{r.design_code}</td>
              <td className="px-2 py-1.5 text-center border border-gray-400 text-xs">{r.opening || '-'}</td>
              <td className="px-2 py-1.5 text-center border border-gray-400 text-xs">{r.purchase || '-'}</td>
              <td className="px-2 py-1.5 text-center border border-gray-400 text-xs">{r.pur_return || '-'}</td>
              <td className="px-2 py-1.5 text-center border border-gray-400 text-xs">{r.sale || '-'}</td>
              <td className="px-2 py-1.5 text-center border border-gray-400 text-xs">{r.sale_return || '-'}</td>
              <td className="px-2 py-1.5 text-center border border-gray-400 text-xs">{r.reserved || '-'}</td>
              <td className="px-2 py-1.5 text-center font-bold border border-gray-400 text-xs">{r.stock || '-'}</td>
              {/* Sale Aging */}
              {[['sale_lt30','lt30'],['sale_30_60','d30_60'],['sale_60_90','d60_90'],['sale_gt90','gt90']].map(([k,t]) => (
                <td key={k} className={`px-2 py-1.5 text-center border border-gray-400 text-xs font-medium ${getBucketColor(r[k], t)}`}>{r[k] || '-'}</td>
              ))}
              {/* Stock Aging */}
              {[['stock_lt30','lt30'],['stock_30_60','d30_60'],['stock_60_90','d60_90'],['stock_gt90','gt90']].map(([k,t]) => (
                <td key={k} className={`px-2 py-1.5 text-center border border-gray-400 text-xs font-medium ${getBucketColor(r[k], t)}`}>{r[k] || '-'}</td>
              ))}
              <td className="px-2 py-1.5 text-center font-bold border border-gray-400 text-xs">{r.stock_aging_total || '-'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#D5F5E3] font-bold text-xs">
            <td className="px-2 py-2 border border-gray-400"></td>
            <td className="px-2 py-2 border border-gray-400"></td>
            <td className="px-3 py-2 text-right border border-gray-400">TOTAL</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.opening || '-'}</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.purchase || '-'}</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.pur_return || '-'}</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.sale || '-'}</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.sale_return || '-'}</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.reserved || '-'}</td>
            <td className="px-2 py-2 text-center border border-gray-400">{totals.stock || '-'}</td>
            {['sale_lt30','sale_30_60','sale_60_90','sale_gt90','stock_lt30','stock_30_60','stock_60_90','stock_gt90'].map(k => (
              <td key={k} className="px-2 py-2 text-center border border-gray-400">{totals[k] || '-'}</td>
            ))}
            <td className="px-2 py-2 text-center border border-gray-400">{totals.stock_aging_total || '-'}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    {lightbox && (
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black/90 border-none z-[60]" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          <img src={lightbox} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

/* ─── Pending PO Table Component ─── */
const PendingPOTable = ({ vendorId, designImages }) => {
  const [poData, setPOData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/vendor-analytics/${vendorId}/pending-po-data`);
        setPOData(res.data || []);
      } catch { toast.error('Failed to load pending PO data'); }
      setLoading(false);
    };
    fetch();
  }, [vendorId]);

  const { pivot, sizes, designList } = useMemo(() => {
    if (!poData.length) return { pivot: {}, sizes: [], designList: [] };
    const pv = {};
    const allSizes = new Set();
    poData.forEach(pr => {
      const { design_code: dc, color, size, qty_ordered, qty_received, pending, cost } = pr;
      allSizes.add(size);
      if (!pv[dc]) pv[dc] = {};
      if (!pv[dc][color]) pv[dc][color] = {};
      if (!pv[dc][color][size]) pv[dc][color][size] = { ordered: 0, received: 0, pending: 0, cost: 0 };
      const e = pv[dc][color][size];
      e.ordered += qty_ordered; e.received += qty_received; e.pending += pending;
      if (cost) e.cost = cost;
    });
    const sortedSizes = [...allSizes].sort((a, b) => {
      const ai = SIZE_ORDER.indexOf(a), bi = SIZE_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
    return { pivot: pv, sizes: sortedSizes, designList: Object.keys(pv) };
  }, [poData]);

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading pending PO data...</div>;
  if (!poData.length) return <div className="text-center py-8 text-gray-400 text-sm">No pending purchase orders</div>;

  const ROW_TYPES = [
    { key: 'ordered', label: 'Ordered', cls: 'text-blue-700', bg: 'bg-blue-50/40' },
    { key: 'received', label: 'Received', cls: 'text-green-700', bg: 'bg-green-50/40' },
    { key: 'pending', label: 'Pending', cls: 'text-red-600', bg: 'bg-red-50/30' },
  ];
  const ROWS_PER_COLOR = ROW_TYPES.length + 1; // +1 cost row

  // Grand totals
  const grandTotals = {};
  let grandValue = 0;
  sizes.forEach(s => { grandTotals[s] = 0; });
  designList.forEach(dc => Object.keys(pivot[dc]).forEach(c => {
    sizes.forEach(s => {
      const p = pivot[dc][c][s]?.pending || 0;
      grandTotals[s] += p;
      if (p > 0) grandValue += p * (pivot[dc][c][s]?.cost || 0);
    });
  }));
  const grandPendingTotal = Object.values(grandTotals).reduce((s, v) => s + v, 0);

  const formatPending = (val) => {
    if (!val) return <span className="text-gray-300">-</span>;
    if (val < 0) return <span className="text-blue-600 font-bold">{Math.abs(val)}(EX)</span>;
    return <span className="text-red-600 font-bold">{val}</span>;
  };

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-300" data-testid="pending-po-table">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#7D3C98] text-white text-xs uppercase tracking-wider">
              <th className="px-2 py-2.5 text-center font-medium border border-purple-700 w-8">SR</th>
              <th className="px-2 py-2.5 text-center font-medium border border-purple-700 w-14">Image</th>
              <th className="px-3 py-2.5 text-left font-medium border border-purple-700">Design</th>
              <th className="px-3 py-2.5 text-left font-medium border border-purple-700">Colour</th>
              <th className="px-2 py-2.5 text-center font-medium border border-purple-700 w-16">Type</th>
              {sizes.map(s => <th key={s} className="px-1.5 py-2.5 text-center font-medium border border-purple-700 min-w-[40px]">{s}</th>)}
              <th className="px-2 py-2.5 text-center font-medium border border-purple-700">Total</th>
              <th className="px-2 py-2.5 text-right font-medium border border-purple-700">Value</th>
            </tr>
          </thead>
          <tbody>
            {designList.map((dc, di) => {
              const colors = Object.keys(pivot[dc]);
              const totalDesignRows = colors.length * ROWS_PER_COLOR;
              const firstImg = poData.find(p => p.design_code === dc);

              return colors.map((color, ci) => {
                const sizeData = pivot[dc][color];
                // Cost groups for merged display
                const costGroups = [];
                let currentGroup = null;
                sizes.forEach(s => {
                  const v = sizeData[s];
                  if (!v) return;
                  const cost = v.cost || 0;
                  if (currentGroup && currentGroup.cost === cost) { currentGroup.sizes.push(s); }
                  else { currentGroup = { cost, sizes: [s] }; costGroups.push(currentGroup); }
                });

                // Value = sum of positive pending × cost
                const colorValue = sizes.reduce((sum, s) => {
                  const p = sizeData[s]?.pending || 0;
                  return sum + (p > 0 ? p * (sizeData[s]?.cost || 0) : 0);
                }, 0);

                return (
                  <React.Fragment key={`${dc}-${color}`}>
                    {ROW_TYPES.map((rt, rti) => {
                      const rowTotal = sizes.reduce((sum, s) => sum + (sizeData[s]?.[rt.key] || 0), 0);
                      return (
                        <tr key={rt.key} className={rt.bg}>
                          {ci === 0 && rti === 0 && (
                            <>
                              <td className="px-2 py-1.5 text-center text-gray-500 border border-gray-400 font-mono text-xs" rowSpan={totalDesignRows}>{di + 1}</td>
                              <td className="px-1 py-1 text-center border border-gray-400" rowSpan={totalDesignRows}>
                                {designImages?.[dc] ? (
                                  <img src={`${API_BASE}${designImages[dc]}`} alt={dc}
                                    className="w-12 h-12 rounded object-cover cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all mx-auto"
                                    onClick={() => setLightbox(`${API_BASE}${designImages[dc]}`)}
                                    data-testid={`po-img-${dc}`} />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-1.5 font-bold text-gray-900 border border-gray-400" rowSpan={totalDesignRows}>{dc}</td>
                            </>
                          )}
                          {rti === 0 && <td className="px-3 py-1 font-medium text-gray-800 border border-gray-400" rowSpan={ROWS_PER_COLOR}>{color}</td>}
                          <td className={`px-2 py-1 text-xs font-semibold border border-gray-400 ${rt.cls}`}>{rt.label}</td>
                          {sizes.map(s => {
                            const val = sizeData[s]?.[rt.key] || 0;
                            return (
                              <td key={s} className={`px-1.5 py-1 text-center border border-gray-400 text-xs ${rt.key === 'pending' ? '' : `${rt.cls} font-medium`}`}>
                                {rt.key === 'pending' ? formatPending(val) : (val || <span className="text-gray-300">-</span>)}
                              </td>
                            );
                          })}
                          <td className={`px-2 py-1 text-center font-bold border border-gray-400 text-xs ${rt.key === 'pending' ? '' : rt.cls}`}>
                            {rt.key === 'pending' ? formatPending(rowTotal) : rowTotal}
                          </td>
                          {rti === 0 && <td className="px-2 py-1 text-right text-blue-700 border border-gray-400 text-xs font-medium" rowSpan={ROWS_PER_COLOR}>{colorValue ? `₹${colorValue.toLocaleString('en-IN')}` : '-'}</td>}
                        </tr>
                      );
                    })}
                    {/* Cost row */}
                    <tr className="bg-white">
                      <td className="px-2 py-1 text-xs italic text-gray-500 border border-gray-400">Cost</td>
                      {(() => {
                        const cells = [];
                        let sIdx = 0;
                        while (sIdx < sizes.length) {
                          const s = sizes[sIdx];
                          const v = sizeData[s];
                          if (!v) { cells.push(<td key={sIdx} className="border border-gray-400" />); sIdx++; continue; }
                          const group = costGroups.find(g => g.sizes.includes(s));
                          if (group) {
                            let span = 0;
                            for (let i = sIdx; i < sizes.length; i++) { if (group.sizes.includes(sizes[i])) span++; else break; }
                            cells.push(<td key={sIdx} colSpan={span} className="px-1.5 py-1 text-center border border-gray-400">
                              <span className="font-semibold text-purple-800 text-[11px]">@₹{group.cost.toLocaleString('en-IN')}</span>
                            </td>);
                            sIdx += span;
                          } else { cells.push(<td key={sIdx} className="border border-gray-400" />); sIdx++; }
                        }
                        return cells;
                      })()}
                      <td className="border border-gray-400" />
                    </tr>
                  </React.Fragment>
                );
              });
            })}
          </tbody>
          <tfoot>
            <tr className="bg-[#7D3C98] text-white font-bold text-xs">
              <td className="px-2 py-2 border border-purple-700" colSpan={4}></td>
              <td className="px-2 py-2 text-right border border-purple-700">TOTAL</td>
              {sizes.map(s => (
                <td key={s} className="px-1.5 py-2 text-center border border-purple-700">
                  {grandTotals[s] < 0 ? <span className="text-cyan-200">{Math.abs(grandTotals[s])}(EX)</span> : (grandTotals[s] || '-')}
                </td>
              ))}
              <td className="px-2 py-2 text-center border border-purple-700">
                {grandPendingTotal < 0 ? <span className="text-cyan-200">{Math.abs(grandPendingTotal)}(EX)</span> : grandPendingTotal}
              </td>
              <td className="px-2 py-2 text-right border border-purple-700">₹{grandValue.toLocaleString('en-IN')}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black/90 border-none z-[60]" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          {lightbox && <img src={lightbox} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

const VendorStockDetail = ({ user }) => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [sortBy, setSortBy] = useState('stock_value');
  const [lightbox, setLightbox] = useState(null);
  const [showProfit, setShowProfit] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/vendor-analytics/enhanced/${vendorId}`);
        setData(res.data);
      } catch { toast.error('Failed to load vendor data'); }
      setLoading(false);
    };
    fetch();
  }, [vendorId]);

  const pivot = useMemo(() => {
    if (!data?.design_stock_pivot) return [];
    let list = data.design_stock_pivot.map(dp => {
      const t = dp.totals;
      const totalAvail = (t.opening || 0) + (t.purchase || 0);
      const soldPct = totalAvail > 0 ? Math.round(((t.sales || 0) / totalAvail) * 100) : 0;
      return { ...dp, soldPct };
    });
    if (!showAll) list = list.filter(dp => dp.totals.stock > 0);
    if (sortBy === 'stock_value') list.sort((a, b) => b.totals.stock_value - a.totals.stock_value);
    else if (sortBy === 'sold_pct') list.sort((a, b) => b.soldPct - a.soldPct);
    else if (sortBy === 'profit') list.sort((a, b) => b.totals.profit - a.totals.profit);
    return list;
  }, [data, showAll, sortBy]);

  const kpis = useMemo(() => {
    if (!data?.design_stock_pivot) return {};
    const all = data.design_stock_pivot;
    const totalDesigns = all.length;
    const withStock = all.filter(d => d.totals.stock > 0).length;
    const totalStockValue = all.reduce((s, d) => s + (d.totals.stock_value || 0), 0);
    const totalAvailable = all.reduce((s, d) => s + (d.totals.opening || 0) + (d.totals.purchase || 0), 0);
    const totalSold = all.reduce((s, d) => s + (d.totals.sales || 0), 0);
    const avgSoldPct = totalAvailable > 0 ? Math.round((totalSold / totalAvailable) * 100) : 0;
    const mostProfitable = all.reduce((best, d) => d.totals.profit > (best?.totals?.profit || 0) ? d : best, null);
    const totalProfit = all.reduce((s, d) => s + d.totals.profit, 0);
    return { totalDesigns, withStock, totalStockValue, avgSoldPct, mostProfitable, totalProfit, totalSold, totalAvailable };
  }, [data]);

  // Build design_code -> first image mapping for PO table
  const designImages = useMemo(() => {
    if (!data?.design_stock_pivot) return {};
    const map = {};
    data.design_stock_pivot.forEach(dp => {
      if (dp.images?.length && !map[dp.design_code]) map[dp.design_code] = dp.images[0];
    });
    return map;
  }, [data]);

  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedReports, setSelectedReports] = useState({ stock_in_hand: true, stock_aging: false, pending_po: false });
  const [downloading, setDownloading] = useState(false);

  const toggleReport = (key) => setSelectedReports(prev => ({ ...prev, [key]: !prev[key] }));

  const handleDownload = async (format) => {
    const reports = Object.entries(selectedReports).filter(([, v]) => v).map(([k]) => k).join(',');
    if (!reports) { toast.error('Select at least one report'); return; }
    setDownloading(true);
    try {
      const endpoint = format === 'excel' ? 'stock-excel' : 'stock-pdf';
      const res = await api.get(`/vendor-analytics/${vendorId}/${endpoint}?show_profit=${showProfit}&reports=${reports}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data?.vendor_name || 'vendor'}_stock_detail.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
      setDownloadDialogOpen(false);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(false); }
  };

  const getSoldBarColor = (pct) => {
    if (pct >= 70) return 'bg-green-500';
    if (pct >= 40) return 'bg-amber-500';
    return 'bg-red-400';
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return <div className="p-6 text-center text-gray-500">No data found</div>;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1600px] mx-auto" data-testid="vendor-stock-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-to-analytics">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900" data-testid="vendor-stock-title">{data.vendor_name}</h1>
            <p className="text-sm text-gray-500">Stock Detail — Design-wise Pivot</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => setDownloadDialogOpen(true)} data-testid="download-reports-btn">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Download Reports
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stock-kpi-cards">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Designs</p>
            <p className="text-xl font-bold text-indigo-700">{kpis.totalDesigns}</p>
            <p className="text-[10px] text-gray-400">{kpis.withStock} with stock</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Stock Value</p>
            <p className="text-xl font-bold text-amber-700">₹{(kpis.totalStockValue || 0).toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-gray-400">{data.total_stock_qty} pcs</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Avg Sold %</p>
            <p className="text-xl font-bold text-purple-700">{kpis.avgSoldPct}%</p>
            <p className="text-[10px] text-gray-400">{kpis.totalSold}/{kpis.totalAvailable} pcs</p>
          </CardContent>
        </Card>
        {showProfit ? (
          <>
            <Card className={`border-l-4 ${(kpis.totalProfit || 0) >= 0 ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="p-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Profit</p>
                <p className={`text-xl font-bold ${(kpis.totalProfit || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>₹{(kpis.totalProfit || 0).toLocaleString('en-IN')}</p>
                <p className="text-[10px] text-gray-400">from SE</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Most Profitable</p>
                <p className="text-lg font-bold text-emerald-700">{kpis.mostProfitable?.design_code || '-'}</p>
                <p className="text-[10px] text-gray-400">₹{(kpis.mostProfitable?.totals?.profit || 0).toLocaleString('en-IN')}</p>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="border-l-4 border-l-gray-300 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all" onClick={() => setShowProfit(true)} data-testid="kpi-profit-hidden-stock">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Profit</p>
              <p className="text-xl font-bold text-gray-400">* * * *</p>
              <p className="text-[10px] text-indigo-500">Tap to reveal</p>
            </CardContent>
          </Card>
        )}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Delivery Rate</p>
            <p className="text-xl font-bold text-blue-700">{data.delivery_rate_pct}%</p>
            <p className="text-[10px] text-gray-400">{data.qty_received}/{data.qty_ordered}</p>
          </CardContent>
        </Card>
      </div>

      {/* Stock Details Heading with toggles */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-indigo-500 rounded-full" />
          <h2 className="text-lg font-bold text-gray-900">Stock Details — {data.vendor_name}</h2>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-show-all">
            <div className={`relative w-10 h-5 rounded-full transition-colors ${showAll ? 'bg-indigo-500' : 'bg-gray-300'}`}
              onClick={() => setShowAll(!showAll)}>
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${showAll ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-xs text-gray-700">{showAll ? 'Showing all designs' : 'Only with stock'}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-show-profit">
            <div className={`relative w-10 h-5 rounded-full transition-colors ${showProfit ? 'bg-green-500' : 'bg-gray-300'}`}
              onClick={() => setShowProfit(!showProfit)}>
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${showProfit ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-xs text-gray-700">{showProfit ? 'Profit visible' : 'Profit hidden'}</span>
          </label>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-white rounded-lg border p-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Sort by:</span>
          {[
            { key: 'stock_value', label: 'Stock Value' },
            { key: 'sold_pct', label: 'Sold %' },
            { key: 'profit', label: 'Profit' },
          ].map(s => (
            <Button key={s.key} size="sm" variant={sortBy === s.key ? 'default' : 'outline'} className="text-xs h-7"
              onClick={() => setSortBy(s.key)} data-testid={`sort-${s.key}`}>{s.label}</Button>
          ))}
        </div>
        <div className="flex gap-3 text-[10px] text-gray-500 flex-wrap">
          <span><span className="inline-block w-3 h-2 bg-blue-100 rounded mr-1" />Ordered</span>
          <span><span className="inline-block w-3 h-2 bg-cyan-100 rounded mr-1" />Opening</span>
          <span><span className="inline-block w-3 h-2 bg-green-100 rounded mr-1" />Purchase</span>
          <span><span className="inline-block w-3 h-2 bg-red-50 border rounded mr-1" />Pur. Ret.</span>
          <span><span className="inline-block w-3 h-2 bg-purple-100 rounded mr-1" />Sales</span>
          <span><span className="inline-block w-3 h-2 bg-teal-100 rounded mr-1" />Sales Ret.</span>
          <span><span className="inline-block w-3 h-2 bg-amber-100 rounded mr-1" />Stock</span>
          <span><span className="inline-block w-3 h-2 bg-white border rounded mr-1" />Cost</span>
        </div>
      </div>

      {/* Pivot Tables */}
      {pivot.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium">{showAll ? 'No designs found for this vendor' : 'No designs with stock in hand'}</p>
          {!showAll && <Button variant="link" className="text-indigo-600 mt-1" onClick={() => setShowAll(true)}>Show all designs</Button>}
        </div>
      )}

      {pivot.map((dp, dpIdx) => {
        const sizes = [...dp.sizes].sort((a, b) => {
          const ai = SIZE_ORDER.indexOf(a), bi = SIZE_ORDER.indexOf(b);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
        const colorNames = Object.keys(dp.colors);
        const soldPct = dp.soldPct;
        const barColor = getSoldBarColor(soldPct);

        const rows = colorNames.map((color, ci) => {
          const sizeData = dp.colors[color];
          const rowTotals = { ordered: 0, opening: 0, purchase: 0, purchase_return: 0, sales: 0, sales_return: 0, stock: 0 };
          let rowStockValue = 0, rowProfit = 0;
          sizes.forEach(s => {
            const v = sizeData[s] || {};
            rowTotals.ordered += v.ordered || 0;
            rowTotals.opening += v.opening || 0;
            rowTotals.purchase += v.purchase || 0;
            rowTotals.purchase_return += v.purchase_return || 0;
            rowTotals.sales += v.sales || 0;
            rowTotals.sales_return += v.sales_return || 0;
            rowTotals.stock += v.stock || 0;
            rowStockValue += (v.cost || 0) * (v.stock || 0);
            rowProfit += (v.profit_per_pc || 0) * (v.sales || 0);
          });

          const costGroups = [];
          let currentGroup = null;
          sizes.forEach(s => {
            const v = sizeData[s];
            if (!v || (v.ordered === 0 && v.purchase === 0 && v.opening === 0)) return;
            const cost = v.cost || 0;
            if (currentGroup && currentGroup.cost === cost) { currentGroup.sizes.push(s); }
            else { currentGroup = { cost, sizes: [s] }; costGroups.push(currentGroup); }
          });

          const images = dp.images || [];
          const imgColors = dp.image_colors || {};
          const matchedImg = images.find(img => {
            const ic = imgColors[img];
            return ic && ic.toLowerCase() === color.toLowerCase();
          }) || images[0];

          return { sr: ci + 1, color, sizeData, rowTotals, rowStockValue, rowProfit, costGroups, matchedImg };
        });

        const ROW_TYPES = [
          { key: 'ordered', label: 'Ordered', bg: 'bg-blue-50/40', textCls: 'text-blue-700' },
          { key: 'opening', label: 'Opening', bg: 'bg-cyan-50/40', textCls: 'text-cyan-700' },
          { key: 'purchase', label: 'Purchase', bg: 'bg-green-50/40', textCls: 'text-green-700' },
          { key: 'purchase_return', label: 'Pur. Ret.', bg: 'bg-red-50/30', textCls: 'text-red-600' },
          { key: 'sales', label: 'Sales', bg: 'bg-purple-50/40', textCls: 'text-purple-700' },
          { key: 'sales_return', label: 'Sales Ret.', bg: 'bg-teal-50/40', textCls: 'text-teal-700' },
          { key: 'stock', label: 'Stock', bg: 'bg-amber-50/50', textCls: 'text-amber-700', bold: true },
        ];
        const ROWS_PER_COLOR = ROW_TYPES.length + 1; // +1 for cost row

        return (
          <div key={dpIdx} data-testid={`stock-pivot-${dp.design_code}`}>
            <div className="overflow-x-auto rounded-lg border border-gray-300">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                    <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-8">SR</th>
                    <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-14">Image</th>
                    <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Design</th>
                    <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Colour</th>
                    <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-16">Type</th>
                    {sizes.map(s => (
                      <th key={s} className="px-1.5 py-2.5 text-center font-medium border border-gray-700 min-w-[40px]">{s}</th>
                    ))}
                    <th className="px-2 py-2.5 text-center font-medium border border-gray-700">Total</th>
                    <th className="px-2 py-2.5 text-right font-medium border border-gray-700">Value</th>
                    {showProfit && <th className="px-2 py-2.5 text-right font-medium border border-gray-700">Profit</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <React.Fragment key={ri}>
                      {ROW_TYPES.map((rt, rti) => (
                        <tr key={rt.key} className={rt.bg}>
                          {ri === 0 && rti === 0 && (
                            <>
                              <td className="px-2 py-1.5 text-center text-gray-500 border border-gray-400 font-mono text-xs" rowSpan={rows.length * ROWS_PER_COLOR}>{dpIdx + 1}</td>
                              <td className="px-1 py-1 text-center border border-gray-400" rowSpan={rows.length * ROWS_PER_COLOR}>
                                {r.matchedImg ? (
                                  <img src={`${API_BASE}${r.matchedImg}`} alt={dp.design_code}
                                    className="w-12 h-12 rounded object-cover cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all mx-auto"
                                    onClick={() => setLightbox(`${API_BASE}${r.matchedImg}`)}
                                    data-testid={`stock-img-${dp.design_code}`} />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-1.5 font-bold text-gray-900 border border-gray-400" rowSpan={rows.length * ROWS_PER_COLOR}>{dp.design_code}</td>
                            </>
                          )}
                          {rti === 0 && <td className="px-3 py-1 font-medium text-gray-800 border border-gray-400" rowSpan={ROWS_PER_COLOR}>{r.color}</td>}
                          <td className={`px-2 py-1 text-xs font-semibold border border-gray-400 ${rt.textCls}`}>{rt.label}</td>
                          {sizes.map(s => {
                            const v = r.sizeData[s] || {};
                            const val = v[rt.key] || 0;
                            return <td key={s} className={`px-1.5 py-1 text-center border border-gray-400 text-xs ${rt.textCls} ${rt.bold ? 'font-bold' : 'font-medium'}`}>
                              {val || <span className="text-gray-300">-</span>}
                            </td>;
                          })}
                          <td className={`px-2 py-1 text-center font-bold border border-gray-400 text-xs ${rt.textCls}`}>{r.rowTotals[rt.key]}</td>
                          {rti === 0 && <td className="px-2 py-1 text-right text-blue-700 border border-gray-400 text-xs font-medium" rowSpan={ROWS_PER_COLOR}>₹{r.rowStockValue.toLocaleString('en-IN')}</td>}
                          {rti === 0 && showProfit && <td className={`px-2 py-1 text-right border border-gray-400 text-xs font-bold ${r.rowProfit >= 0 ? 'text-green-700' : 'text-red-600'}`} rowSpan={ROWS_PER_COLOR}>₹{r.rowProfit.toLocaleString('en-IN')}</td>}
                        </tr>
                      ))}
                      {/* Cost row */}
                      <tr className="bg-white">
                        <td className="px-2 py-1 text-xs italic text-gray-500 border border-gray-400">Cost</td>
                        {(() => {
                          const cells = [];
                          let sIdx = 0;
                          while (sIdx < sizes.length) {
                            const s = sizes[sIdx];
                            const v = r.sizeData[s];
                            if (!v || (v.ordered === 0 && v.purchase === 0 && v.opening === 0)) { cells.push(<td key={sIdx} className="border border-gray-400" />); sIdx++; continue; }
                            const group = r.costGroups.find(g => g.sizes.includes(s));
                            if (group) {
                              let span = 0;
                              for (let i = sIdx; i < sizes.length; i++) { if (group.sizes.includes(sizes[i])) span++; else break; }
                              cells.push(<td key={sIdx} colSpan={span} className="px-1.5 py-1 text-center border border-gray-400">
                                <span className="font-semibold text-indigo-800 text-[11px]">@₹{group.cost.toLocaleString('en-IN')}</span>
                              </td>);
                              sIdx += span;
                            } else { cells.push(<td key={sIdx} className="border border-gray-400" />); sIdx++; }
                          }
                          return cells;
                        })()}
                        <td className="border border-gray-400" />
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#218C59] text-white font-bold text-xs">
                    <td className="px-2 py-2 border border-green-700 align-top" colSpan={4}>
                      <div className="flex items-center h-full">TOTAL — {dp.design_code}</div>
                    </td>
                    <td className="px-1 py-2 border border-green-700 align-top text-[10px] leading-relaxed">
                      <div>Ordered</div>
                      <div className="text-cyan-200">Opening</div>
                      <div className="text-green-200">Purchase</div>
                      <div className="text-red-200">Pur. Ret.</div>
                      <div className="text-purple-200">Sales</div>
                      <div className="text-teal-200">Sales Ret.</div>
                      <div className="text-yellow-200">Stock</div>
                    </td>
                    {sizes.map(s => {
                      const colTotals = {};
                      ROW_TYPES.forEach(rt => {
                        colTotals[rt.key] = colorNames.reduce((sum, c) => sum + (dp.colors[c][s]?.[rt.key] || 0), 0);
                      });
                      return (
                        <td key={s} className="px-1.5 py-2 text-center border border-green-700 leading-relaxed align-top">
                          <div>{colTotals.ordered}</div>
                          <div className="text-cyan-200">{colTotals.opening}</div>
                          <div className="text-green-200">{colTotals.purchase}</div>
                          <div className="text-red-200">{colTotals.purchase_return > 0 ? `-${colTotals.purchase_return}` : '—'}</div>
                          <div className="text-purple-200">{colTotals.sales}</div>
                          <div className="text-teal-200">{colTotals.sales_return > 0 ? `+${colTotals.sales_return}` : '—'}</div>
                          <div className="text-yellow-200 font-extrabold">{colTotals.stock}</div>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-center border border-green-700 leading-relaxed align-top">
                      <div>{dp.totals.ordered}</div>
                      <div className="text-cyan-200">{dp.totals.opening}</div>
                      <div className="text-green-200">{dp.totals.purchase}</div>
                      <div className="text-red-200">{dp.totals.purchase_return > 0 ? `-${dp.totals.purchase_return}` : '—'}</div>
                      <div className="text-purple-200">{dp.totals.sales}</div>
                      <div className="text-teal-200">{dp.totals.sales_return > 0 ? `+${dp.totals.sales_return}` : '—'}</div>
                      <div className="text-yellow-200 font-extrabold">{dp.totals.stock}</div>
                    </td>
                    <td className="px-2 py-2 text-right border border-green-700 align-top">₹{(dp.totals.stock_value || 0).toLocaleString('en-IN')}</td>
                    {showProfit && <td className="px-2 py-2 text-right border border-green-700 align-top">₹{dp.totals.profit.toLocaleString('en-IN')}</td>}
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Sold % Progress Bar */}
            <div className="mt-2 mb-5 px-1" data-testid={`sold-bar-${dp.design_code}`}>
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>{soldPct >= 70 ? 'Fast Moving' : soldPct >= 40 ? 'Moderate' : 'Slow Moving'}</span>
                <span className="font-semibold">Sold: {soldPct}% ({dp.totals.sales}/{(dp.totals.opening || 0) + (dp.totals.purchase || 0)} available)</span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${Math.min(soldPct, 100)}%` }} />
              </div>
            </div>
          </div>
        );
      })}

      {/* ─── Sale & Stock Aging Section ─── */}
      <div className="mt-8" data-testid="aging-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-1 h-6 bg-[#1A5276] rounded-full" />
          <h2 className="text-lg font-bold text-gray-900">Design Wise Clubbed Report — Sale & Stock Ageing</h2>
        </div>
        <StockAgingTable vendorId={vendorId} designImages={designImages} />
      </div>

      {/* ─── Pending PO Section ─── */}
      <div className="mt-8" data-testid="pending-po-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-1 h-6 bg-[#7D3C98] rounded-full" />
          <h2 className="text-lg font-bold text-gray-900">Pending Purchase Order Report</h2>
        </div>
        <PendingPOTable vendorId={vendorId} designImages={designImages} />
      </div>

      {/* Download Reports Dialog */}
      <Dialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogTitle className="text-lg font-bold text-gray-900">Download Reports</DialogTitle>
          <p className="text-sm text-gray-500 -mt-2">Select which reports to include in the download</p>
          <div className="space-y-3 mt-2">
            {[
              { key: 'stock_in_hand', label: 'Stock in Hand Report', desc: 'Current stock levels by design, color & size', color: 'text-emerald-700' },
              { key: 'stock_aging', label: 'Stock Aging Report', desc: 'Style-wise clubbed: Opening, Purchase, Sale, Sale Ageing & Stock Ageing (<30, 30-60, 60-90, >90 days)', color: 'text-blue-700' },
              { key: 'pending_po', label: 'Pending Purchase Order Report', desc: 'All POs with ordered vs received quantities', color: 'text-purple-700' },
            ].map(r => (
              <label key={r.key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedReports[r.key] ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`} data-testid={`report-checkbox-${r.key}`}>
                <input type="checkbox" checked={selectedReports[r.key]} onChange={() => toggleReport(r.key)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
                <div>
                  <span className={`font-semibold text-sm ${r.color}`}>{r.label}</span>
                  <p className="text-xs text-gray-400 mt-0.5">{r.desc}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" className="flex-1 gap-1 bg-green-700 hover:bg-green-800 text-white" onClick={() => handleDownload('excel')} disabled={downloading} data-testid="download-excel-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              {downloading ? 'Downloading...' : 'Excel'}
            </Button>
            <Button size="sm" className="flex-1 gap-1 bg-red-700 hover:bg-red-800 text-white" onClick={() => handleDownload('pdf')} disabled={downloading} data-testid="download-pdf-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              {downloading ? 'Downloading...' : 'PDF'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black/90 border-none z-[60]" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          {lightbox && <img src={lightbox} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" data-testid="stock-lightbox-img" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorStockDetail;
